import type { Href } from "expo-router";
import { COLORS } from "../../../lib/brand";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useState } from "react";
import { ActivityIndicator, Platform, ScrollView, Text, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { Button } from "../../../components/ui/Button";
import { useAuth } from "../../../lib/authContext";
import {
  acceptInvite,
  fetchInvitePreview,
  type TeamInvitePreview,
} from "../../../lib/teamInvites";

const FOREST = COLORS.FOREST.hex;
const AMBER = COLORS.AMBER.hex;
const SAND_DARK = "#D9D2C5";
const TEXT_MUTED = "rgba(28,43,26,0.62)";

export default function TeamInviteAcceptScreen() {
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const { user, loading: authLoading } = useAuth();
  const [preview, setPreview] = useState<TeamInvitePreview | null | undefined>(undefined);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug || typeof slug !== "string") {
      setPreview(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const r = await fetchInvitePreview(slug);
      if (!cancelled) setPreview(r);
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // If signed-out, send to login with a redirect_to so we come right back.
  useEffect(() => {
    if (authLoading) return;
    if (!user && slug && typeof slug === "string") {
      const target = `/teams/invite/${slug}`;
      router.replace(`/login?next=${encodeURIComponent(target)}` as Href);
    }
  }, [authLoading, user, slug]);

  async function onAccept() {
    if (!slug || typeof slug !== "string") return;
    setAccepting(true);
    setError(null);
    const res = await acceptInvite(slug);
    setAccepting(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    // Land on the team workspace. We prefer the new nested route
    // (/student/competition/[id]/team) when the preview gave us the comp
    // id; otherwise fall through to the legacy redirect which will look
    // the comp up from the team row.
    const compId = preview?.competition_id ?? null;
    if (compId) {
      router.replace(`/student/competition/${compId}/team` as Href);
    } else {
      router.replace(`/student/team/${res.teamId}` as Href);
    }
  }

  const loadingState = preview === undefined || authLoading || !user;

  return (
    <SafeAreaView className="flex-1 bg-sand" edges={["top", "left", "right"]}>
      <ScrollView contentContainerStyle={{ flexGrow: 1 }}>
        <View className="mx-auto w-full max-w-[640px] flex-1 px-5 py-12 md:px-8">
          {/* Header */}
          <View className="flex-row items-center gap-2">
            <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
            <Text
              className="text-[10px] font-bold uppercase tracking-widest-3"
              style={{ fontFamily: "DMSans_700Bold", color: AMBER, letterSpacing: 2 }}
            >
              Team invite
            </Text>
          </View>
          <Text
            className="mt-3 text-[34px] leading-tight text-forest"
            style={{ fontFamily: "CormorantGaramond_400Regular" }}
          >
            You've been{" "}
            <Text style={{ fontFamily: "CormorantGaramond_400Regular", fontStyle: "italic", color: AMBER }}>
              invited
            </Text>
            .
          </Text>

          {loadingState ? (
            <View className="mt-12 items-center">
              <ActivityIndicator size="large" color={FOREST} />
            </View>
          ) : !preview ? (
            <View
              className="mt-8 rounded-card border bg-white px-5 py-6"
              style={{ borderColor: SAND_DARK }}
            >
              <Text
                className="text-[10px] font-bold uppercase"
                style={{ fontFamily: "DMSans_700Bold", color: AMBER, letterSpacing: 1.4 }}
              >
                Invite not found
              </Text>
              <Text
                className="mt-2 text-[14px] leading-6"
                style={{ fontFamily: "DMSans_400Regular", color: "rgba(28,43,26,0.78)" }}
              >
                This invite link doesn't match anything. Ask the captain to send you a fresh one.
              </Text>
              <View className="mt-5 self-start">
                <Button variant="link" onPress={() => router.replace("/student" as Href)}>
                  ← Go to dashboard
                </Button>
              </View>
            </View>
          ) : (
            <View
              className="mt-8 overflow-hidden rounded-card border bg-white"
              style={{ borderColor: SAND_DARK }}
            >
              {/* Top: case + team summary */}
              <View className="border-b px-6 pt-6 pb-5" style={{ borderBottomColor: "rgba(28,43,26,0.08)" }}>
                <Text
                  className="text-[10px] font-bold uppercase"
                  style={{ fontFamily: "DMSans_700Bold", color: AMBER, letterSpacing: 1.6 }}
                >
                  {preview.competition_name ?? "Competition"}
                </Text>
                <Text
                  className="mt-1.5 text-[26px] leading-tight text-forest"
                  style={{ fontFamily: "CormorantGaramond_500Medium" }}
                  numberOfLines={2}
                >
                  Join "{preview.team_name ?? "Team"}"
                </Text>
                <Text
                  className="mt-2 text-[13px] leading-5"
                  style={{ fontFamily: "DMSans_400Regular", color: TEXT_MUTED }}
                >
                  {preview.captain_name ? (
                    <>
                      Invited by{" "}
                      <Text style={{ fontFamily: "DMSans_600SemiBold", color: FOREST }}>
                        {preview.captain_name}
                      </Text>
                      {" "}· captain of this team.
                    </>
                  ) : (
                    "The team captain sent you this invite."
                  )}
                </Text>
                {preview.member_count != null && preview.team_size_max != null ? (
                  <Text
                    className="mt-1 text-[12px]"
                    style={{ fontFamily: "DMSans_400Regular", color: TEXT_MUTED }}
                  >
                    Roster: {preview.member_count} of {preview.team_size_max} members
                  </Text>
                ) : null}
              </View>

              {/* Validity / accept area */}
              <View className="px-6 py-5">
                {preview.is_valid ? (
                  <>
                    <Text
                      className="text-[14px] leading-6"
                      style={{ fontFamily: "DMSans_400Regular", color: "rgba(28,43,26,0.78)" }}
                    >
                      Accepting will{" "}
                      <Text style={{ fontFamily: "DMSans_600SemiBold", color: FOREST }}>
                        register you for the case
                      </Text>{" "}
                      and add you to the team. You'll need to meet the case eligibility filters.
                    </Text>
                    {error ? (
                      <View
                        className="mt-3 rounded-md border px-3 py-2"
                        style={{ borderColor: "rgba(192,57,43,0.30)", backgroundColor: "rgba(192,57,43,0.05)" }}
                      >
                        <Text
                          className="text-[12.5px]"
                          style={{ fontFamily: "DMSans_500Medium", color: "#C0392B" }}
                        >
                          {error}
                        </Text>
                      </View>
                    ) : null}
                    <View className="mt-5 flex-row flex-wrap items-center gap-2">
                      <Button variant="primary" loading={accepting} onPress={() => void onAccept()}>
                        Accept & join team
                      </Button>
                      <Button variant="link" onPress={() => router.replace("/student" as Href)}>
                        Not now
                      </Button>
                    </View>
                  </>
                ) : (
                  <>
                    <View
                      className="rounded-md border px-3 py-2.5"
                      style={{
                        borderColor: "rgba(181,135,58,0.35)",
                        backgroundColor: "rgba(181,135,58,0.06)",
                      }}
                    >
                      <Text
                        className="text-[10px] font-bold uppercase"
                        style={{ fontFamily: "DMSans_700Bold", color: AMBER, letterSpacing: 1.4 }}
                      >
                        Invite unavailable
                      </Text>
                      <Text
                        className="mt-1 text-[13px] leading-5"
                        style={{ fontFamily: "DMSans_400Regular", color: "rgba(28,43,26,0.78)" }}
                      >
                        {preview.invalid_reason ?? "This invite is no longer valid."}
                      </Text>
                    </View>
                    <View className="mt-5 self-start">
                      <Button
                        variant="primary"
                        onPress={() =>
                          router.replace(
                            preview.competition_id
                              ? (`/student/competition/${preview.competition_id}` as Href)
                              : ("/student" as Href)
                          )
                        }
                      >
                        Browse other teams in this case
                      </Button>
                    </View>
                  </>
                )}
              </View>
            </View>
          )}

          <View className="mt-6">
            <Button variant="link" onPress={() => router.replace("/student" as Href)}>
              ← Skip and go to dashboard
            </Button>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

// Suppress unused import warnings on platforms where we don't need it.
void Platform;
